import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check, Download, FileUp, GraduationCap, Link2, Plus, School, Trash2, Undo2, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, apiUrl, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { PersonFieldListItem } from '../lib/checkouts';
import { formatDate, formatDateTime, formatNumber, formatRelative, formatUtcDate, PERSON_TYPE_LABEL, personName } from '../lib/format';
import { useBuildings, useDebouncedValue } from '../lib/hooks';
import {
  gradeLabel,
  ROLE_LABELS,
  schoolYearLabel,
  uploadRosterFile,
  useRosterOverview,
  useRosterSchools,
  vendorGuide,
  VENDORS,
  type OneRosterIntegration,
  type OneRosterRole,
  type OneRosterSettings,
  type RollupOverview,
  type RosterClassDetail,
  type RosterClassItem,
  type RosterRollup,
  type RosterTransfer,
  type SisVendor,
} from '../lib/oneroster';
import type { Page, PersonType } from '../lib/types';
import { PersonTypeBadge } from './badges';
import { DataSourcePicker, useIntegrationMutations } from './integration-shell';
import { Instructions, RemoveCredentials, Step, useCredentials } from './mdm-integrations';
import { useToast } from './toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  cn,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageLoader,
  Pagination,
  Segmented,
  Select,
  Spinner,
  Switch,
  Table,
  Td,
  Th,
} from './ui';

const megabytes = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

function useInvalidateRoster() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['oneroster'] });
    void queryClient.invalidateQueries({ queryKey: ['integrations'] });
    void queryClient.invalidateQueries({ queryKey: ['people'] });
  };
}

// ---------------------------------------------------------------------------
// Connection
// ---------------------------------------------------------------------------

function VendorPicker({ value, onChange, disabled }: { value: SisVendor; onChange(vendor: SisVendor): void; disabled: boolean }) {
  return (
    <div role="radiogroup" aria-label="Student information system" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
      {VENDORS.map((vendor) => (
        <button
          key={vendor.value}
          type="button"
          role="radio"
          aria-checked={value === vendor.value}
          disabled={disabled}
          onClick={() => onChange(vendor.value)}
          className={cn(
            'rounded-lg border px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed',
            value === vendor.value ? 'border-brand-500 bg-brand-50/60 ring-1 ring-brand-500' : 'border-slate-200 hover:border-slate-300',
          )}
        >
          <span className="block text-sm font-medium text-slate-900">{vendor.label}</span>
          <span className="block text-xs text-slate-500">
            OneRoster {vendor.version} · {vendor.auth === 'OAUTH1' ? 'OAuth 1.0a' : 'OAuth 2'}
          </span>
        </button>
      ))}
    </div>
  );
}

function ApiConnection({ integration }: { integration: OneRosterIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations('ONEROSTER');
  const { save, remove } = useCredentials('ONEROSTER');
  const s = integration.settings;
  const saved = integration.oneRoster;
  const [vendor, setVendor] = useState<SisVendor>(s.vendor);
  const [baseUrl, setBaseUrl] = useState(s.baseUrl ?? '');
  const [version, setVersion] = useState(s.version);
  const [authMethod, setAuthMethod] = useState(s.authMethod);
  const [tokenUrl, setTokenUrl] = useState(s.tokenUrl ?? '');
  const [scope, setScope] = useState(s.scope ?? '');
  const [clientId, setClientId] = useState(saved.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const guide = vendorGuide(vendor);

  const chooseVendor = (next: SisVendor) => {
    setVendor(next);
    const nextGuide = vendorGuide(next);
    setVersion(nextGuide.version);
    setAuthMethod(nextGuide.auth);
    if (!s.tokenUrl) setTokenUrl('');
    if (!s.scope) setScope('');
  };

  const tokenPlaceholder = vendor === s.vendor && saved.defaultTokenUrl ? `${saved.defaultTokenUrl} (used when blank)` : (guide.tokenHint ?? 'Worked out from the API address when blank');

  return (
    <>
      <Step number={1} title="Your student information system">
        <VendorPicker value={vendor} onChange={chooseVendor} disabled={!isAdmin} />
        <Instructions steps={guide.steps} />
      </Step>

      <Step number={2} title="OneRoster API">
        <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
          <Field label="API address" className="sm:col-span-2" hint={saved.apiUrl && vendor === s.vendor ? `Syncs call ${saved.apiUrl}` : 'The server alone is fine; the OneRoster path is added for you.'}>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} disabled={!isAdmin} placeholder={guide.addressHint} className="font-mono text-xs" />
          </Field>
          <Field label="OneRoster version">
            <Select value={version} onChange={(e) => setVersion(e.target.value as typeof version)} disabled={!isAdmin}>
              <option value="1.1">1.1 (/ims/oneroster/v1p1)</option>
              <option value="1.2">1.2 (/ims/oneroster/rostering/v1p2)</option>
            </Select>
          </Field>
          <Field label="Sign-in">
            <Select value={authMethod} onChange={(e) => setAuthMethod(e.target.value as typeof authMethod)} disabled={!isAdmin}>
              <option value="OAUTH2">OAuth 2 client credentials</option>
              <option value="OAUTH1">OAuth 1.0a signed requests</option>
            </Select>
          </Field>
          {authMethod === 'OAUTH2' && (
            <>
              <Field label="Token URL" className="sm:col-span-2" hint={guide.tokenHint ? 'Required: your SIS shows it with the client ID.' : 'Leave blank to use your SIS’s usual token address.'}>
                <Input value={tokenUrl} onChange={(e) => setTokenUrl(e.target.value)} disabled={!isAdmin} placeholder={tokenPlaceholder} className="font-mono text-xs" />
              </Field>
              <Field label="Scope" className="sm:col-span-2" hint={guide.scope ? 'Leave blank to use the scope shown.' : 'Optional. Only some SISs need one.'}>
                <Input value={scope} onChange={(e) => setScope(e.target.value)} disabled={!isAdmin} placeholder={guide.scope ?? saved.defaultScope ?? ''} className="font-mono text-xs" />
              </Field>
            </>
          )}
        </div>
        {isAdmin && (
          <Button
            loading={update.isPending}
            onClick={() => update.mutate({ settings: { vendor, source: 'API', baseUrl, version, authMethod, tokenUrl, scope } })}
          >
            Save API settings
          </Button>
        )}
      </Step>

      <Step number={3} title="Client credentials">
        {saved.secretsUnreadable && <Alert tone="red">The saved credentials can’t be read. Enter them again.</Alert>}
        <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
          <Field label="Client ID (key)">
            <Input value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" autoComplete="off" />
          </Field>
          <Field label="Client secret">
            <Input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              disabled={!isAdmin}
              placeholder={saved.hasClientSecret ? 'Saved - leave blank to keep' : ''}
              autoComplete="new-password"
            />
          </Field>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!clientId.trim()}
              onClick={() => save.mutate({ clientId, ...(clientSecret ? { clientSecret } : {}) }, { onSuccess: () => setClientSecret('') })}
            >
              Save credentials
            </Button>
            {(saved.clientId || saved.hasClientSecret) && (
              <RemoveCredentials provider="ONEROSTER" remove={remove} description="Syncs stop until new credentials are saved. People already synced stay in Edvora." />
            )}
          </div>
        )}
      </Step>
    </>
  );
}

function CsvConnection({ integration }: { integration: OneRosterIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const toast = useToast();
  const invalidate = useInvalidateRoster();
  const { update } = useIntegrationMutations('ONEROSTER');
  const { save } = useCredentials('ONEROSTER');
  const overview = useRosterOverview();
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [mode, setMode] = useState<'UPLOAD' | 'LINK'>(integration.settings.csvUrl ? 'LINK' : 'UPLOAD');
  const [csvUrl, setCsvUrl] = useState(integration.settings.csvUrl ?? '');
  const [username, setUsername] = useState(integration.oneRoster.csvUsername ?? '');
  const [password, setPassword] = useState('');

  const upload = async (file: File) => {
    if (file.size > 150 * 1024 * 1024) {
      toast.error('Roster files can be up to 150 MB. Zip the CSV files to make them smaller.');
      return;
    }
    setProgress(0);
    try {
      const result = await uploadRosterFile(file, setProgress);
      toast.success(result.syncQueued ? `Uploaded ${file.name} - importing it now` : `Uploaded ${file.name}`);
      invalidate();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setProgress(null);
    }
  };

  const uploads = overview.data?.uploads ?? [];

  return (
    <>
      <Step number={1} title="Roster files">
        <p className="max-w-3xl text-sm text-slate-600">
          For an SIS without a OneRoster API. Use a <strong>OneRoster CSV export</strong> (a zip of orgs.csv, users.csv, classes.csv, enrollments.csv, and
          academicSessions.csv, version 1.1 or 1.2), or <strong>one CSV of people</strong> with columns like First Name, Last Name, Email, Role, Grade, and
          School. Extra columns can fill in person fields.
        </p>
        <Segmented<'UPLOAD' | 'LINK'>
          className="max-w-md"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'UPLOAD', label: 'Upload files', icon: <FileUp /> },
            { value: 'LINK', label: 'Download from a link', icon: <Link2 /> },
          ]}
        />
      </Step>

      {mode === 'UPLOAD' ? (
        <Step number={2} title="Upload a roster">
          <div className="flex flex-wrap items-center gap-3">
            {isAdmin && (
              <>
                <Button variant="primary" icon={<FileUp />} loading={progress !== null} onClick={() => input.current?.click()}>
                  Choose a .zip or .csv
                </Button>
                <input
                  ref={input}
                  type="file"
                  accept=".zip,.csv,text/csv,application/zip"
                  className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void upload(file);
                  }}
                />
              </>
            )}
            <a href={apiUrl('/oneroster/csv-template')} className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:underline">
              <Download className="size-4" /> CSV template
            </a>
            {integration.settings.csvUrl && <Badge tone="amber">Syncs download from the link instead</Badge>}
          </div>
          {progress !== null && (
            <div className="max-w-md space-y-1" role="status">
              <p className="text-xs text-slate-500">Uploading… {Math.round(progress * 100)}%</p>
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full rounded-full bg-brand-600 transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            </div>
          )}
          {uploads.length === 0 ? (
            <p className="text-sm text-slate-500">Nothing uploaded yet. Uploading a file imports it right away, once the integration is turned on.</p>
          ) : (
            <ul className="max-w-3xl divide-y divide-slate-100 rounded-lg border border-slate-200">
              {uploads.map((file) => (
                <li key={file.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
                  <span className="min-w-0 flex-1 truncate font-medium text-slate-900">{file.fileName}</span>
                  {file.id === overview.data?.latestUploadId && <Badge tone="green">Syncs use this</Badge>}
                  <span className="text-xs text-slate-500">
                    {file.summary} · {megabytes(file.sizeBytes)} · {file.uploadedByName}, {formatRelative(file.createdAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {isAdmin && integration.settings.csvUrl && (
            <Button variant="ghost" onClick={() => update.mutate({ settings: { csvUrl: '' } }, { onSuccess: () => setCsvUrl('') })}>
              Use uploaded files instead of the link
            </Button>
          )}
        </Step>
      ) : (
        <Step number={2} title="Roster file link">
          <p className="max-w-3xl text-sm text-slate-600">
            If your SIS publishes its export at an HTTPS address, each sync downloads the latest file from it, so the nightly sync stays current without
            uploads.
          </p>
          <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
            <Field label="Link to the .zip or .csv" className="sm:col-span-2">
              <Input value={csvUrl} onChange={(e) => setCsvUrl(e.target.value)} disabled={!isAdmin} placeholder="https://sis.example.org/exports/oneroster.zip" className="font-mono text-xs" />
            </Field>
            <Field label="Username" hint="Optional, if the link needs a sign-in">
              <Input value={username} onChange={(e) => setUsername(e.target.value)} disabled={!isAdmin} autoComplete="off" />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!isAdmin || !username}
                placeholder={integration.oneRoster.hasCsvPassword ? 'Saved - leave blank to keep' : ''}
                autoComplete="new-password"
              />
            </Field>
          </div>
          {isAdmin && (
            <Button
              variant="primary"
              loading={update.isPending || save.isPending}
              onClick={() =>
                update.mutate(
                  { settings: { source: 'CSV', csvUrl } },
                  { onSuccess: () => save.mutate({ csvUsername: username, ...(password ? { csvPassword: password } : {}) }, { onSuccess: () => setPassword('') }) },
                )
              }
            >
              Save link
            </Button>
          )}
        </Step>
      )}
    </>
  );
}

export function OneRosterConnection({ integration }: { integration: OneRosterIntegration }) {
  const { can } = useAuth();
  const { update } = useIntegrationMutations('ONEROSTER');
  const source = integration.settings.source;
  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
        {integration.mode === 'DEMO' && <p className="mt-2 text-xs text-slate-500">Demo data is a district of four schools with 900 students, 90 staff, and their classes.</p>}
      </section>
      {integration.mode === 'LIVE' && (
        <>
          <section>
            <h3 className="mb-3 text-sm font-semibold text-slate-900">Where rosters come from</h3>
            <Segmented<'API' | 'CSV'>
              className="max-w-md"
              value={source}
              onChange={(next) => can('ADMIN') && next !== source && update.mutate({ settings: { source: next } })}
              options={[
                { value: 'API', label: 'SIS API (OneRoster)' },
                { value: 'CSV', label: 'CSV files' },
              ]}
            />
          </section>
          {source === 'API' ? <ApiConnection integration={integration} /> : <CsvConnection integration={integration} />}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roster: what synced, and each school's building
// ---------------------------------------------------------------------------

function Stat({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="rounded-lg border border-slate-200 px-4 py-3">
      <p className="text-2xl font-semibold text-slate-900 tabular-nums">{typeof value === 'number' ? formatNumber(value) : value}</p>
      <p className="text-xs text-slate-500">{label}</p>
      {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

export function OneRosterRoster({ integration }: { integration: OneRosterIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const toast = useToast();
  const invalidate = useInvalidateRoster();
  const overview = useRosterOverview();
  const schools = useRosterSchools();
  const buildings = useBuildings();
  const link = useMutation({
    mutationFn: ({ id, buildingId }: { id: string; buildingId: string | null }) => api.put(`/oneroster/schools/${id}`, { buildingId }),
    onSuccess: () => {
      invalidate();
      toast.success('School matched');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (overview.error) return <ErrorState error={overview.error} onRetry={() => overview.refetch()} />;
  if (!overview.data) return <PageLoader />;
  const data = overview.data;
  const active = data.people.active;
  const staff = (active.TEACHER ?? 0) + (active.STAFF ?? 0);
  const synced = Object.values(active).reduce((sum, n) => sum + (n ?? 0), 0) + data.people.inactive;

  if (synced === 0 && data.schools === 0) {
    return (
      <EmptyState
        icon={<GraduationCap />}
        title="Nothing synced yet"
        description={integration.enabled ? 'Run a sync to bring in schools, people, and classes.' : 'Set up the connection, turn the integration on, and sync.'}
      />
    );
  }

  return (
    <div className="space-y-6">
      {data.rollup && (
        <Alert tone="blue" icon={<GraduationCap />} title={`The ${schoolYearLabel(data.rollup.toYear)} roll-up is in progress`}>
          Students stay in their new grades until your SIS moves to the new school year, or {formatUtcDate(data.rollup.holdUntil)}.
        </Alert>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Students" value={active.STUDENT ?? 0} />
        <Stat label="Teachers and staff" value={staff} />
        <Stat label="Schools" value={data.schools} />
        <Stat label="Classes" value={data.classes} />
        <Stat label="Enrollments" value={data.enrollments} />
        <Stat label="School changes" value={data.transfersLast30Days} sub="in the last 30 days" />
      </div>
      <p className="text-xs text-slate-500">
        {data.schoolYear ? `The SIS is in the ${schoolYearLabel(data.schoolYear)} school year. ` : ''}
        {formatNumber(data.people.inactive)} people who left the SIS are kept as inactive, with their history.{' '}
        <Link to="/people?source=ONEROSTER" className="font-medium text-brand-700 hover:underline">
          See synced people
        </Link>
      </p>

      <section>
        <h3 className="text-sm font-semibold text-slate-900">Schools</h3>
        <p className="mb-3 text-sm text-slate-500">Match each school to a building, so its people and classes line up with your rooms and devices. New schools match a building with the same name.</p>
        {schools.isLoading ? (
          <Spinner />
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <Table>
              <thead>
                <tr>
                  <Th>School</Th>
                  <Th>Students</Th>
                  <Th>Staff</Th>
                  <Th>Classes</Th>
                  <Th>Building</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(schools.data ?? []).map((school) => (
                  <tr key={school.id} className={cn(!school.active && 'opacity-60')}>
                    <Td>
                      <Link to={`/people?schoolId=${school.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                        {school.name}
                      </Link>
                      <p className="text-xs text-slate-500">
                        {school.identifier ? `School ${school.identifier} · ` : ''}
                        {school.active ? `SIS ID ${school.sourcedId}` : 'No longer in the SIS'}
                      </p>
                    </Td>
                    <Td className="tabular-nums">{formatNumber(school.students)}</Td>
                    <Td className="tabular-nums">{formatNumber(school.staff)}</Td>
                    <Td className="tabular-nums">{formatNumber(school.classes)}</Td>
                    <Td className="w-56">
                      {isAdmin ? (
                        <Select
                          aria-label={`Building for ${school.name}`}
                          value={school.building?.id ?? ''}
                          disabled={link.isPending}
                          onChange={(e) => link.mutate({ id: school.id, buildingId: e.target.value || null })}
                        >
                          <option value="">No building</option>
                          {buildings.data?.map((building) => (
                            <option key={building.id} value={building.id}>
                              {building.name}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        (school.building?.name ?? <span className="text-slate-400">No building</span>)
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Roles and sync options
// ---------------------------------------------------------------------------

const ROLE_ORDER: OneRosterRole[] = ['student', 'teacher', 'administrator', 'aide', 'proctor', 'guardian', 'parent', 'relative'];
const TYPE_CHOICES: PersonType[] = ['STUDENT', 'TEACHER', 'STAFF', 'OTHER'];

export function OneRosterOptions({ integration }: { integration: OneRosterIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations('ONEROSTER');
  const schools = useRosterSchools();
  const s = integration.settings;
  const [roleTypes, setRoleTypes] = useState(s.roleTypes);
  const [limit, setLimit] = useState(s.schoolIds.length > 0);
  const [schoolIds, setSchoolIds] = useState<string[]>(s.schoolIds);
  const [syncClasses, setSyncClasses] = useState(s.syncClasses);
  const [deactivateMissing, setDeactivateMissing] = useState(s.deactivateMissing);
  const [safety, setSafety] = useState(String(s.departureSafetyPercent));
  const [matchExisting, setMatchExisting] = useState(s.matchExisting);
  const [pageSize, setPageSize] = useState(String(s.pageSize));

  const known = (schools.data ?? []).filter((school) => school.active);

  return (
    <div className="space-y-8">
      <section>
        <h3 className="text-sm font-semibold text-slate-900">Who syncs</h3>
        <p className="mb-3 text-sm text-slate-500">Choose what each OneRoster role becomes in Edvora, or leave it out.</p>
        <div className="max-w-xl overflow-hidden rounded-lg border border-slate-200">
          <Table>
            <thead>
              <tr>
                <Th>OneRoster role</Th>
                <Th>Syncs as</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {ROLE_ORDER.map((role) => (
                <tr key={role}>
                  <Td className="font-medium text-slate-900">{ROLE_LABELS[role]}</Td>
                  <Td className="w-56">
                    <Select
                      aria-label={`${ROLE_LABELS[role]} sync as`}
                      value={roleTypes[role] ?? ''}
                      disabled={!isAdmin}
                      onChange={(e) => setRoleTypes({ ...roleTypes, [role]: (e.target.value || null) as PersonType | null })}
                    >
                      <option value="">Don’t sync</option>
                      {TYPE_CHOICES.map((type) => (
                        <option key={type} value={type}>
                          {PERSON_TYPE_LABEL[type]}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </section>

      <section className="max-w-xl space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Schools</h3>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="radio" checked={!limit} disabled={!isAdmin} onChange={() => setLimit(false)} /> Every school in the SIS
        </label>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="radio" checked={limit} disabled={!isAdmin || known.length === 0} onChange={() => setLimit(true)} /> Only the schools I choose
        </label>
        {known.length === 0 && <p className="text-xs text-slate-500">Schools appear here after the first sync.</p>}
        {limit && (
          <div className="grid gap-1.5 rounded-lg border border-slate-200 p-3 sm:grid-cols-2">
            {known.map((school) => (
              <label key={school.id} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="size-4 rounded border-slate-300 accent-brand-700"
                  checked={schoolIds.includes(school.sourcedId)}
                  disabled={!isAdmin}
                  onChange={(e) => setSchoolIds(e.target.checked ? [...schoolIds, school.sourcedId] : schoolIds.filter((id) => id !== school.sourcedId))}
                />
                <span className="truncate">{school.name}</span>
              </label>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-xl space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Sync options</h3>
        <Switch label="Sync classes and enrollments" description="Shows each person’s classes and teachers." checked={syncClasses} disabled={!isAdmin} onChange={setSyncClasses} />
        <Switch
          label="Link people already in Edvora"
          description="People added by hand or imported are matched by email or ID number and kept, with their history, instead of added again."
          checked={matchExisting}
          disabled={!isAdmin}
          onChange={setMatchExisting}
        />
        <Switch
          label="Mark people who leave the SIS inactive"
          description="Withdrawn students and departed staff become inactive. Their checkout history stays."
          checked={deactivateMissing}
          disabled={!isAdmin}
          onChange={setDeactivateMissing}
        />
        {deactivateMissing && (
          <Field label="Safety limit (%)" hint="If a sync would mark more than this share of synced people inactive, it marks no one, in case the SIS export was incomplete.">
            <Input type="number" min={1} max={100} value={safety} onChange={(e) => setSafety(e.target.value)} disabled={!isAdmin} className="w-28" />
          </Field>
        )}
        <Field label="Records per request" hint="How many records each API request or file page holds. Lower it if your SIS times out.">
          <Input type="number" min={100} max={10000} step={100} value={pageSize} onChange={(e) => setPageSize(e.target.value)} disabled={!isAdmin} className="w-28" />
        </Field>
      </section>

      {isAdmin && (
        <Button
          variant="primary"
          loading={update.isPending}
          disabled={limit && schoolIds.length === 0}
          onClick={() =>
            update.mutate({
              settings: {
                roleTypes,
                schoolIds: limit ? schoolIds : [],
                syncClasses,
                deactivateMissing,
                departureSafetyPercent: Number(safety) || 20,
                matchExisting,
                pageSize: Number(pageSize) || 1000,
              } satisfies Partial<OneRosterSettings>,
            })
          }
        >
          Save roster options
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Person fields from SIS metadata
// ---------------------------------------------------------------------------

export function OneRosterFields({ integration }: { integration: OneRosterIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations('ONEROSTER');
  const overview = useRosterOverview();
  const fields = useQuery({
    queryKey: ['person-fields', 'all'],
    queryFn: () => api.get<{ items: PersonFieldListItem[] }>('/person-fields', { archived: true }),
  });
  const [rows, setRows] = useState(integration.settings.fieldMappings.length ? integration.settings.fieldMappings : [{ key: '', fieldId: '' }]);
  const keys = overview.data?.metadataKeys ?? [];
  const fieldList = (fields.data?.items ?? []).filter((field) => !field.archived);
  const fieldName = useMemo(() => new Map((fields.data?.items ?? []).map((field) => [field.id, field.name])), [fields.data]);
  const mappedKeys = new Map(integration.settings.fieldMappings.map((row) => [row.key.replace(/^metadata\./i, '').toLowerCase(), row.fieldId]));
  const complete = rows.filter((row) => row.key.trim() && row.fieldId);

  return (
    <div className="space-y-8">
      <section className="max-w-3xl space-y-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">SIS metadata to person fields</h3>
          <p className="text-sm text-slate-500">
            Your SIS can send extra details with each person, like free/reduced lunch status, a cohort, or a bus route. Map a metadata key to a{' '}
            <Link to="/settings?tab=checkouts" className="font-medium text-brand-700 hover:underline">
              person field
            </Link>{' '}
            and every sync fills it in. The SIS owns mapped fields: a value it stops sending is cleared.
          </p>
        </div>
        <datalist id="oneroster-metadata-keys">
          {keys.map((entry) => (
            <option key={entry.key} value={entry.key}>
              e.g. {entry.sample}
            </option>
          ))}
        </datalist>
        <div className="space-y-2">
          {rows.map((row, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2">
              <Input
                list="oneroster-metadata-keys"
                aria-label={`Metadata key ${index + 1}`}
                placeholder="Metadata key, e.g. freeReducedLunch"
                value={row.key}
                disabled={!isAdmin}
                onChange={(e) => setRows(rows.map((item, i) => (i === index ? { ...item, key: e.target.value } : item)))}
                className="w-full font-mono text-xs sm:w-64"
              />
              <ArrowRight className="hidden size-4 text-slate-400 sm:block" />
              <Select
                aria-label={`Person field ${index + 1}`}
                value={row.fieldId}
                disabled={!isAdmin}
                onChange={(e) => setRows(rows.map((item, i) => (i === index ? { ...item, fieldId: e.target.value } : item)))}
                className="w-full sm:w-56"
              >
                <option value="">Choose a person field</option>
                {fieldList.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.name}
                  </option>
                ))}
              </Select>
              {isAdmin && (
                <Button size="sm" variant="ghost" icon={<Trash2 />} aria-label={`Remove mapping ${index + 1}`} onClick={() => setRows(rows.filter((_, i) => i !== index))} />
              )}
            </div>
          ))}
        </div>
        {fields.data && fieldList.length === 0 && (
          <Alert tone="amber">
            There are no person fields yet.{' '}
            <Link to="/settings?tab=checkouts" className="font-medium underline">
              Add one in Settings
            </Link>{' '}
            first.
          </Alert>
        )}
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button icon={<Plus />} onClick={() => setRows([...rows, { key: '', fieldId: '' }])}>
              Add a mapping
            </Button>
            <Button variant="primary" loading={update.isPending} onClick={() => update.mutate({ settings: { fieldMappings: complete } })}>
              Save mappings
            </Button>
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-slate-900">Metadata keys in the last sync</h3>
        <p className="mb-3 text-sm text-slate-500">What your SIS sent, and how many people had each.</p>
        {keys.length === 0 ? (
          <p className="text-sm text-slate-500">None yet. Keys appear here after a sync.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <Table>
              <thead>
                <tr>
                  <Th>Key</Th>
                  <Th>Example</Th>
                  <Th>People</Th>
                  <Th>Fills in</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {keys.map((entry) => {
                  const fieldId = mappedKeys.get(entry.key.toLowerCase());
                  return (
                    <tr key={entry.key}>
                      <Td className="font-mono text-xs text-slate-900">{entry.key}</Td>
                      <Td className="max-w-[16rem] truncate text-slate-600">{entry.sample}</Td>
                      <Td className="tabular-nums">{formatNumber(entry.count)}</Td>
                      <Td>{fieldId ? <Badge tone="green">{fieldName.get(fieldId) ?? 'A deleted field'}</Badge> : <span className="text-slate-400">—</span>}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Classes
// ---------------------------------------------------------------------------

function ClassRoster({ classId, onClose }: { classId: string; onClose(): void }) {
  const detail = useQuery({ queryKey: ['oneroster', 'class', classId], queryFn: () => api.get<RosterClassDetail>(`/oneroster/classes/${classId}`) });
  const klass = detail.data;
  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={klass?.title ?? 'Class'}
      description={klass && [klass.classCode, klass.school?.name, klass.periods.length ? `Period ${klass.periods.join(', ')}` : null, klass.location, klass.schoolYear && schoolYearLabel(klass.schoolYear)].filter(Boolean).join(' · ')}
    >
      {!klass ? (
        detail.error ? <ErrorState error={detail.error} /> : <Spinner />
      ) : klass.enrollments.length === 0 ? (
        <EmptyState icon={<Users />} title="No one is in this class" />
      ) : (
        <ul className="divide-y divide-slate-100">
          {klass.enrollments.map((enrollment) => (
            <li key={enrollment.id} className="flex items-center gap-3 py-2 text-sm">
              <Link to={`/people/${enrollment.person.id}`} className="min-w-0 flex-1 truncate font-medium text-slate-900 hover:text-brand-700">
                {personName(enrollment.person)}
              </Link>
              {enrollment.role === 'student' ? (
                <span className="text-xs text-slate-500">{gradeLabel(enrollment.person.gradeLevel)}</span>
              ) : (
                <Badge tone="violet">{enrollment.role === 'teacher' ? (enrollment.primary ? 'Teacher' : 'Co-teacher') : enrollment.role}</Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

export function OneRosterClasses() {
  const schools = useRosterSchools();
  const [search, setSearch] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const query = useDebouncedValue(search.trim(), 300);
  useEffect(() => setPage(1), [query, schoolId]);
  const classes = useQuery({
    queryKey: ['oneroster', 'classes', query, schoolId, page],
    queryFn: () => api.get<Page<RosterClassItem>>('/oneroster/classes', { search: query, schoolId, page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search classes, codes, or rooms" className="w-full sm:w-72" aria-label="Search classes" />
        <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} className="w-auto max-w-full" aria-label="School">
          <option value="">Every school</option>
          {(schools.data ?? [])
            .filter((school) => school.active)
            .map((school) => (
              <option key={school.id} value={school.id}>
                {school.name}
              </option>
            ))}
        </Select>
        {classes.isFetching && <Spinner className="size-4" />}
      </div>
      {classes.error ? (
        <ErrorState error={classes.error} onRetry={() => classes.refetch()} />
      ) : !classes.data ? (
        <Spinner />
      ) : classes.data.items.length === 0 ? (
        <EmptyState icon={<School />} title="No classes" description={query || schoolId ? 'Nothing matches.' : 'Classes appear after a sync, when your SIS sends them.'} />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <Table>
            <thead>
              <tr>
                <Th>Class</Th>
                <Th>Teacher</Th>
                <Th>School</Th>
                <Th>Students</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {classes.data.items.map((klass) => (
                <tr key={klass.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(klass.id)}>
                  <Td>
                    <button type="button" className="text-left font-medium text-slate-900 hover:text-brand-700" onClick={() => setOpen(klass.id)}>
                      {klass.title}
                    </button>
                    <p className="text-xs text-slate-500">
                      {[klass.classCode, klass.periods.length ? `Period ${klass.periods.join(', ')}` : null, klass.location].filter(Boolean).join(' · ')}
                    </p>
                  </Td>
                  <Td className="text-slate-600">
                    {klass.teachers.map((teacher) => personName(teacher)).join(', ') || <span className="text-slate-400">—</span>}
                  </Td>
                  <Td className="max-w-[14rem] truncate text-slate-600">{klass.school?.name ?? '—'}</Td>
                  <Td className="tabular-nums">{formatNumber(klass.studentCount)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={page} pageSize={25} total={classes.data.total} onPage={setPage} />
        </div>
      )}
      {open && <ClassRoster classId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Transfers
// ---------------------------------------------------------------------------

export function OneRosterTransfers() {
  const schools = useRosterSchools();
  const [search, setSearch] = useState('');
  const [schoolId, setSchoolId] = useState('');
  const [page, setPage] = useState(1);
  const query = useDebouncedValue(search.trim(), 300);
  useEffect(() => setPage(1), [query, schoolId]);
  const transfers = useQuery({
    queryKey: ['oneroster', 'transfers', query, schoolId, page],
    queryFn: () => api.get<Page<RosterTransfer>>('/oneroster/transfers', { search: query, schoolId, page, pageSize: 25 }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-slate-500">
        People the sync found at a different school than before. Check whether their devices should move with them, or come back.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people" className="w-full sm:w-72" aria-label="Search transfers" />
        <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} className="w-auto max-w-full" aria-label="School">
          <option value="">Any school</option>
          {(schools.data ?? []).map((school) => (
            <option key={school.id} value={school.id}>
              {school.name}
            </option>
          ))}
        </Select>
      </div>
      {transfers.error ? (
        <ErrorState error={transfers.error} onRetry={() => transfers.refetch()} />
      ) : !transfers.data ? (
        <Spinner />
      ) : transfers.data.items.length === 0 ? (
        <EmptyState icon={<ArrowRight />} title="No school changes" description="When a student or staff member moves schools in the SIS, it shows here." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <Table>
            <thead>
              <tr>
                <Th>Person</Th>
                <Th>Moved</Th>
                <Th>Devices out</Th>
                <Th>When</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {transfers.data.items.map((transfer) => (
                <tr key={transfer.id}>
                  <Td>
                    <Link to={`/people/${transfer.person.id}`} className="font-medium text-slate-900 hover:text-brand-700">
                      {personName(transfer.person)}
                    </Link>
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                      <PersonTypeBadge type={transfer.person.type} />
                      {gradeLabel(transfer.person.gradeLevel)}
                    </p>
                  </Td>
                  <Td className="text-slate-600">
                    {transfer.fromSchoolName} <ArrowRight className="inline size-3.5 text-slate-400" /> <span className="font-medium text-slate-900">{transfer.toSchoolName}</span>
                  </Td>
                  <Td>{transfer.person.activeCheckoutCount > 0 ? <Badge tone="amber">{transfer.person.activeCheckoutCount}</Badge> : <span className="text-slate-400">None</span>}</Td>
                  <Td className="whitespace-nowrap text-slate-500">{formatDate(transfer.detectedAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={page} pageSize={25} total={transfers.data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Year-end roll-up
// ---------------------------------------------------------------------------

function RollupHistory({ history }: { history: RosterRollup[] }) {
  if (history.length === 0) return null;
  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold text-slate-900">Past roll-ups</h3>
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <Table>
          <thead>
            <tr>
              <Th>School year</Th>
              <Th>Moved up</Th>
              <Th>Graduated</Th>
              <Th>Classes archived</Th>
              <Th>Run</Th>
              <Th>Finished</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {history.map((rollup) => (
              <tr key={rollup.id}>
                <Td className="font-medium text-slate-900">{schoolYearLabel(rollup.toYear)}</Td>
                <Td className="tabular-nums">{formatNumber(rollup.promoted)}</Td>
                <Td className="tabular-nums">{formatNumber(rollup.graduated)}</Td>
                <Td className="tabular-nums">{formatNumber(rollup.classesArchived)}</Td>
                <Td className="text-xs text-slate-500">
                  {rollup.createdByName}, {formatDateTime(rollup.createdAt)}
                </Td>
                <Td className="text-xs text-slate-500">
                  {rollup.finishedAt ? (
                    <>
                      {formatDate(rollup.finishedAt)}
                      {rollup.finishReason && <span className="block">{rollup.finishReason}</span>}
                    </>
                  ) : (
                    <Badge tone="blue">In progress</Badge>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </section>
  );
}

export function OneRosterRollup() {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const toast = useToast();
  const invalidate = useInvalidateRoster();
  const overview = useQuery({ queryKey: ['oneroster', 'rollup'], queryFn: () => api.get<RollupOverview>('/oneroster/rollup') });
  const [form, setForm] = useState<RollupOverview['suggested'] | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    if (overview.data && !form) setForm(overview.data.suggested);
  }, [overview.data, form]);

  const apply = useMutation({
    mutationFn: () => api.post<RosterRollup>('/oneroster/rollup', form),
    onSuccess: (rollup) => {
      invalidate();
      setConfirming(false);
      toast.success(`${formatNumber(rollup.promoted)} students moved up and ${formatNumber(rollup.graduated)} graduated`);
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(errorMessage(err));
    },
  });
  const finish = useMutation({
    mutationFn: () => api.post('/oneroster/rollup/finish'),
    onSuccess: () => {
      invalidate();
      setFinishing(false);
      toast.success('Roll-up finished. Syncs use the SIS’s grades again.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (overview.error) return <ErrorState error={overview.error} onRetry={() => overview.refetch()} />;
  if (!overview.data || !form) return <PageLoader />;
  const data = overview.data;
  const students = data.grades.reduce((sum, row) => sum + row.count, 0);
  const graduating = data.grades.find((row) => row.grade === form.graduatingGrade)?.count ?? 0;
  const gradeIndex = (grade: string | null) => (grade ? (['PK', 'TK', 'KG', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13'] as string[]).indexOf(grade) : -1);
  const graduatingIndex = gradeIndex(form.graduatingGrade);
  const movingUp = data.grades.filter((row) => gradeIndex(row.grade) >= 0 && gradeIndex(row.grade) < graduatingIndex).reduce((sum, row) => sum + row.count, 0);
  const archiving = data.classYears.find((row) => row.schoolYear === form.fromYear)?.count ?? 0;

  return (
    <div className="space-y-8">
      <section className="max-w-3xl space-y-2">
        <h3 className="text-sm font-semibold text-slate-900">The summer between school years</h3>
        <p className="text-sm text-slate-600">
          Most SIS platforms keep last year’s grades and classes until the new school year starts. A roll-up moves students up a grade, graduates your
          graduating grade to alumni, and archives last year’s classes now. Until your SIS catches up, nightly syncs keep the roll-up in place and
          don’t mark anyone inactive, since summer rosters are often incomplete. The roll-up ends on its own once the SIS shows students in their new grades,
          or on the hold date.
        </p>
      </section>

      {data.active ? (
        <Alert
          tone="blue"
          icon={<GraduationCap />}
          title={`The ${schoolYearLabel(data.active.toYear)} roll-up is in progress`}
        >
          <p>
            {formatNumber(data.active.promoted)} students moved up, {formatNumber(data.active.graduated)} graduated, and {formatNumber(data.active.classesArchived)} classes were
            archived. Syncs hold it until the SIS moves to the new year, or {formatUtcDate(data.active.holdUntil)}.
          </p>
          {isAdmin && (
            <Button size="sm" className="mt-3" icon={<Undo2 />} onClick={() => setFinishing(true)}>
              Finish now and use the SIS’s grades
            </Button>
          )}
        </Alert>
      ) : (
        <>
          <section className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="School year ending">
                  <Input
                    inputMode="numeric"
                    maxLength={4}
                    value={form.fromYear}
                    disabled={!isAdmin}
                    onChange={(e) => {
                      const fromYear = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setForm({ ...form, fromYear, toYear: fromYear.length === 4 ? String(Number(fromYear) + 1) : form.toYear });
                    }}
                  />
                </Field>
                <Field label="New school year">
                  <Input value={schoolYearLabel(form.toYear)} disabled />
                </Field>
              </div>
              <Field label="Graduating grade" hint="Students in this grade become alumni and inactive.">
                <Select value={form.graduatingGrade} disabled={!isAdmin} onChange={(e) => setForm({ ...form, graduatingGrade: e.target.value })}>
                  {['05', '06', '08', '12', '13'].map((grade) => (
                    <option key={grade} value={grade}>
                      {gradeLabel(grade)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Hold until" hint="The latest a roll-up waits for your SIS, usually the first day of school.">
                <Input type="date" value={form.holdUntil} disabled={!isAdmin} onChange={(e) => setForm({ ...form, holdUntil: e.target.value })} className="w-48" />
              </Field>
              <Switch
                label={`Archive ${schoolYearLabel(form.fromYear)} classes`}
                description="Their enrollments end too. People keep their history."
                checked={form.archiveClasses}
                disabled={!isAdmin}
                onChange={(archiveClasses) => setForm({ ...form, archiveClasses })}
              />
            </div>

            <Card className="p-5">
              <h4 className="text-sm font-semibold text-slate-900">What happens</h4>
              <ul className="mt-3 space-y-2 text-sm text-slate-700">
                <li className="flex gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                  {formatNumber(movingUp)} students move up a grade
                </li>
                <li className="flex gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                  {formatNumber(graduating)} {gradeLabel(form.graduatingGrade).toLowerCase()} students graduate
                </li>
                <li className="flex gap-2">
                  <Check className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                  {form.archiveClasses ? `${formatNumber(archiving)} ${schoolYearLabel(form.fromYear)} classes are archived` : 'Classes stay as they are'}
                </li>
                {students - movingUp - graduating > 0 && (
                  <li className="flex gap-2 text-slate-500">
                    <span className="mt-0.5 size-4 shrink-0 text-center">–</span>
                    {formatNumber(students - movingUp - graduating)} students in other grades stay where they are
                  </li>
                )}
              </ul>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {data.grades.map((row) => (
                  <span key={row.grade ?? 'none'} className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600">
                    {row.grade ? gradeLabel(row.grade) : 'No grade'}: <span className="font-semibold text-slate-900">{formatNumber(row.count)}</span>
                  </span>
                ))}
              </div>
            </Card>
          </section>
          {isAdmin && (
            <Button variant="primary" icon={<GraduationCap />} disabled={students === 0 || form.fromYear.length !== 4 || !form.holdUntil} onClick={() => setConfirming(true)}>
              Roll up to {schoolYearLabel(form.toYear)}
            </Button>
          )}
          {students === 0 && <p className="text-sm text-slate-500">There are no active students from the SIS to roll up yet.</p>}
        </>
      )}

      <RollupHistory history={data.history} />

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => apply.mutate()}
        loading={apply.isPending}
        tone="primary"
        title={`Roll up to ${schoolYearLabel(form.toYear)}?`}
        description={`${formatNumber(movingUp)} students move up a grade and ${formatNumber(graduating)} graduate. This can’t be undone, but the SIS’s grades take over again once it moves to the new school year.`}
        confirmLabel="Roll up"
      />
      <ConfirmDialog
        open={finishing}
        onClose={() => setFinishing(false)}
        onConfirm={() => finish.mutate()}
        loading={finish.isPending}
        tone="primary"
        title="Finish the roll-up?"
        description="The next sync uses the grades and classes your SIS has now. Do this once your SIS has moved to the new school year."
        confirmLabel="Finish roll-up"
      />
    </div>
  );
}
